/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DocumentEditModal.tsx
 🗓️ Created: 2026-01-21
 🧩 Description:
    Modal for editing context documents, viewing prompts,
    selecting AI profiles, and reprocessing documents.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Cpu, Play, Save, AlertCircle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { Label } from '../../../components/ui/label';
import type { ContextDocument, DynamicContext } from '../api/aiAgentsService';
import type { AiProfile } from '../../ai-profiles/api/aiProfilesApi';

interface DocumentEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    document: ContextDocument | null;
    contexts: DynamicContext[];
    aiProfiles: AiProfile[];
    defaultPrompt: string;
    onSavePrompt: (prompt: string) => Promise<void>;
    onProcess: (profileId: number) => Promise<void>;
}

export default function DocumentEditModal({
    isOpen,
    onClose,
    document,
    contexts,
    aiProfiles,
    defaultPrompt,
    onSavePrompt,
    onProcess
}: DocumentEditModalProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const [prompt, setPrompt] = useState('');
    const [saving, setSaving] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

    useEffect(() => {
        if (document) {
            setPrompt(document.extractionPrompt || defaultPrompt);
        }
    }, [document, defaultPrompt]);

    if (!document) return null;

    const getStatusIcon = () => {
        switch (document.processingStatus) {
            case 'completed':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'processing':
                return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
            case 'failed':
                return <AlertCircle className="h-4 w-4 text-red-500" />;
            default:
                return <Clock className="h-4 w-4 text-amber-500" />;
        }
    };

    const getStatusText = () => {
        switch (document.processingStatus) {
            case 'completed':
                return t('aiAgents.dynamicContext.statusCompleted') || 'Completed';
            case 'processing':
                return t('aiAgents.dynamicContext.statusProcessing') || 'Processing...';
            case 'failed':
                return t('aiAgents.dynamicContext.statusFailed') || 'Failed';
            default:
                return t('aiAgents.dynamicContext.statusPending') || 'Pending';
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSavePrompt(prompt);
            showSuccess(t('aiAgents.messages.promptSaved') || 'Prompt saved successfully');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to save prompt';
            showError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleProcess = async () => {
        if (!selectedProfileId) {
            showError(t('aiAgents.dynamicContext.noProfileSelected') || 'No AI Profile selected. Please select an AI Assistant profile.');
            return;
        }
        setProcessing(true);
        try {
            await onProcess(selectedProfileId);
            showSuccess(t('aiAgents.messages.documentProcessed') || 'Document processing started');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to process document';
            showError(message);
        } finally {
            setProcessing(false);
        }
    };

    // Get contexts extracted from this document
    const documentContexts = contexts.filter(ctx => String(ctx.sourceDocumentId) === String(document.id));

    // Format file size
    const formatFileSize = (bytes: number | null): string => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[2500]"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 z-[2500] flex items-center justify-center p-4 pt-20">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: 'spring', duration: 0.3 }}
                            className="bg-card border border-border rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/10 rounded-lg">
                                        <FileText className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground">
                                            {document.originalFilename}
                                        </h3>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <span>{document.fileType.toUpperCase()}</span>
                                            <span>•</span>
                                            <span>{formatFileSize(document.fileSize)}</span>
                                            <span>•</span>
                                            <span className="flex items-center gap-1">
                                                {getStatusIcon()}
                                                {getStatusText()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                                {/* Extraction Prompt */}
                                <div>
                                    <Label className="mb-2 block">
                                        {t('aiAgents.dynamicContext.extractionPrompt') || 'Extraction Prompt'}
                                    </Label>
                                    <textarea
                                        value={prompt}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        className="w-full h-32 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none font-mono"
                                        placeholder={t('aiAgents.dynamicContext.extractionPromptPlaceholder') || 'Enter instructions for extracting context from this document...'}
                                    />
                                </div>

                                {/* AI Profile Selector */}
                                <div>
                                    <Label className="mb-2 block">
                                        {t('aiAgents.dynamicContext.aiProfile') || 'AI Profile for Processing'}
                                    </Label>
                                    <select
                                        className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                        value={selectedProfileId ?? ''}
                                        onChange={(e) => setSelectedProfileId(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">{t('common.selectOption') || 'Select an option'}</option>
                                        {aiProfiles
                                            .filter(p =>
                                                p.type === 'general' &&
                                                !p.model?.toLowerCase().includes('realtime') &&
                                                !p.profile_name?.toLowerCase().includes('real time') &&
                                                !p.profile_name?.toLowerCase().includes('realtime')
                                            )
                                            .map(profile => (
                                                <option key={profile.id} value={profile.id}>
                                                    {profile.profile_name || `Profile ${profile.id}`} ({profile.model})
                                                </option>
                                            ))}
                                    </select>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('aiAgents.dynamicContext.aiProfileHelp') || 'Select which AI profile will be used to process and extract context from this document.'}
                                    </p>
                                </div>

                                {/* Processing Error - only show if status is failed */}
                                {document.processingStatus === 'failed' && document.processingError && (
                                    <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
                                        <div className="flex items-center gap-2 text-destructive">
                                            <AlertCircle className="h-4 w-4" />
                                            <span className="font-medium">{t('common.error') || 'An error occurred'}</span>
                                        </div>
                                        <p className="text-sm text-destructive/80 mt-1">{document.processingError}</p>
                                    </div>
                                )}

                                {/* Extracted Contexts */}
                                {documentContexts.length > 0 && (
                                    <div>
                                        <Label className="mb-2 block">
                                            {t('aiAgents.dynamicContext.extractedContexts') || 'Extracted Contexts'} ({documentContexts.length})
                                        </Label>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {documentContexts.map(ctx => (
                                                <div
                                                    key={ctx.id}
                                                    className="p-3 bg-muted/30 border border-border rounded-md"
                                                >
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="font-medium text-sm">{ctx.contextName}</span>
                                                        <span className="text-xs text-muted-foreground">Key: {ctx.contextKey}</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                                        {ctx.contextContent}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-border">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {t('common.cancel') || 'Cancel'}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-accent hover:bg-accent/80 rounded-md transition-colors disabled:opacity-50"
                                >
                                    {saving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4" />
                                    )}
                                    {t('common.save') || 'Save'}
                                </button>
                                <button
                                    onClick={handleProcess}
                                    disabled={processing || document.processingStatus === 'processing'}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50"
                                >
                                    {processing ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Cpu className="h-4 w-4" />
                                    )}
                                    {t('aiAgents.dynamicContext.reprocess') || 'Reprocess'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
